import React, { useEffect, useState, useRef } from "react";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ImageIcon,
  Film,
  UploadCloud,
  RotateCcw,
} from "lucide-react";
import { API, resolveImage } from "../products";
import { heroCollage, heroImages, aboutVideo } from "../mock";

const TOKEN_KEY = "tcs_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

const SLOTS = [
  { key: "hero_1", label: "Hero image 1 (large)", type: "image", fallback: heroCollage[0] },
  { key: "hero_2", label: "Hero image 2", type: "image", fallback: heroCollage[1] },
  { key: "hero_3", label: "Hero image 3", type: "image", fallback: heroCollage[2] },
  { key: "about_poster", label: "About — video thumbnail (poster)", type: "image", fallback: heroImages.about },
  { key: "about_video", label: "About — video", type: "video", fallback: aboutVideo },
];

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [toast, onDone]);
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div
      data-testid="media-toast"
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white font-semibold ${ok ? "bg-green-600" : "bg-red-600"}`}
    >
      {ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      {toast.msg}
    </div>
  );
}

const MediaSlot = ({ slot, url, onUpload, onReset, notify }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const isVideo = slot.type === "video";
  const src = url ? resolveImage(url) : slot.fallback;
  const overridden = !!url;

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    await onUpload(slot, file);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`media-slot-${slot.key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
          {isVideo ? <Film className="w-4 h-4 text-green-600" /> : <ImageIcon className="w-4 h-4 text-green-600" />}
          {slot.label}
        </span>
        <span className={`text-[10px] font-bold ${overridden ? "text-green-600" : "text-slate-400"}`}>
          {overridden ? "CUSTOM" : "DEFAULT"}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={isVideo ? "video/*" : "image/*"}
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
        data-testid={`media-file-${slot.key}`}
      />

      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files?.[0]); }}
        data-testid={`media-dropzone-${slot.key}`}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed overflow-hidden bg-slate-50 flex items-center justify-center transition-colors ${
          isVideo ? "aspect-video" : "aspect-[4/3]"
        } ${dragging ? "border-green-500 bg-green-50" : "border-slate-300 hover:border-green-400"}`}
      >
        {src ? (
          isVideo ? (
            <video key={src} src={src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={src} alt={slot.label} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="text-slate-400 text-center px-3">
            <UploadCloud className="w-6 h-6 mx-auto mb-1" />
            <p className="text-xs">Drag &amp; drop or click</p>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-slate-900/60 text-white text-[11px] py-1 text-center flex items-center justify-center gap-1">
          <UploadCloud className="w-3.5 h-3.5" /> Drag &amp; drop or click to replace
        </div>
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-green-600" />
          </div>
        )}
      </div>

      {overridden && (
        <button
          onClick={() => onReset(slot)}
          data-testid={`media-reset-${slot.key}`}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-red-600"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reset to default
        </button>
      )}
    </div>
  );
};

export default function AdminSiteMedia() {
  const [media, setMedia] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const notify = (type, msg) => setToast({ type, msg });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/site/media`);
      const data = await res.json();
      setMedia(data && typeof data === "object" ? data : {});
    } catch (e) {
      notify("error", "Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (slot, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/site/media/${slot.key}`, { method: "POST", headers: authHeaders(), body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "upload");
      }
      const data = await res.json();
      setMedia((prev) => ({ ...prev, [slot.key]: data.url }));
      notify("ok", `${slot.label} updated`);
    } catch (e) {
      notify("error", typeof e.message === "string" && e.message !== "upload" ? e.message : "Upload failed");
    }
  };

  const onReset = async (slot) => {
    try {
      const res = await fetch(`${API}/admin/site/media/${slot.key}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("reset");
      setMedia((prev) => {
        const next = { ...prev };
        delete next[slot.key];
        return next;
      });
      notify("ok", "Reset to default");
    } catch (e) {
      notify("error", "Reset failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-site-media">
      <div>
        <h2 className="font-extrabold text-slate-800 text-lg">Homepage Media</h2>
        <p className="text-sm text-slate-500">Replace the hero images and the About video (and its thumbnail). Images are auto-compressed; changes go live immediately.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-14 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SLOTS.map((slot) => (
            <MediaSlot
              key={slot.key}
              slot={slot}
              url={media[slot.key]}
              onUpload={onUpload}
              onReset={onReset}
              notify={notify}
            />
          ))}
        </div>
      )}

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}
